const STORAGE_KEY = 'product_view_context'
const SESSION_KEY = 'pv_session_id'
const TTL_MS = 5 * 60 * 1000

/**
 * UI blocks a product page visit can originate from.
 * Must stay in sync with the PHP whitelist in
 * pimway/app/Services/ProductViews/ProductViewBlock.php — unknown values are rejected.
 */
export const PRODUCT_VIEW_BLOCKS = {
  categoryGrid: 'category_grid',
  categoryGridColors: 'category_grid_colors',
  collectionPage: 'collection_page',
  productPageColors: 'product_page_colors',
  calculatorPanel: 'calculator_panel',
  recommendedProducts: 'recommended_products',
  lastVisited: 'last_visited',
  miniProductsGrid: 'mini_products_grid',
  search: 'search',
  shopTheLook: 'shop_the_look',
  betterWith: 'better_with',
  fixFogCalculator: 'fix_fog_calculator',
}

const KNOWN_BLOCKS = new Set(Object.values(PRODUCT_VIEW_BLOCKS))

const canUseStorage = () => typeof localStorage !== 'undefined'

/**
 * Absolute URL of the page the click happened on. `document.referrer` is useless
 * here: it does not change on SPA navigation between routes.
 */
const currentPageUrl = () => {
  if (typeof window === 'undefined' || !window.location) {
    return null
  }

  try {
    const {origin, pathname, search} = window.location
    return `${origin}${pathname}${search}`.slice(0, 500)
  } catch {
    return null
  }
}

export const getProductViewSessionId = () => {
  if (!canUseStorage()) {
    return null
  }

  try {
    const cartToken = localStorage.getItem('cart_token')
    if (cartToken) {
      return cartToken
    }

    let sessionId = localStorage.getItem(SESSION_KEY)
    if (!sessionId) {
      sessionId = `pv_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
      localStorage.setItem(SESSION_KEY, sessionId)
    }

    return sessionId
  } catch {
    return null
  }
}

/**
 * Parse category filter segments from a path like /klinker/size=45x45/color=gra_vit/
 */
export const filtersFromCategoryPath = (path) => {
  const filters = {}

  String(path || '')
    .split('/')
    .filter(Boolean)
    .forEach((part) => {
      if (!part.includes('=')) {
        return
      }

      const eq = part.indexOf('=')
      const key = part.slice(0, eq)
      const raw = part.slice(eq + 1)

      if (!key || !raw) {
        return
      }

      // Multi-select values are joined with `_` in the URL.
      // Numeric ranges like `8_12` stay as a single string.
      if (/^\d+(?:\.\d+)?_\d+(?:\.\d+)?$/.test(raw)) {
        filters[key] = raw
      } else if (raw.includes('_')) {
        filters[key] = raw.split('_').filter(Boolean)
      } else {
        filters[key] = [raw]
      }
    })

  return Object.keys(filters).length ? filters : null
}

const normalizeFilters = (filters) => {
  if (!filters || typeof filters !== 'object' || Array.isArray(filters)) {
    return null
  }

  const clean = {}

  for (const [key, value] of Object.entries(filters)) {
    if (!key) {
      continue
    }

    if (typeof value === 'string' && value.trim()) {
      clean[key] = value.trim()
      continue
    }

    if (Array.isArray(value)) {
      const values = value
        .filter((item) => item !== null && item !== undefined && String(item).trim() !== '')
        .map((item) => String(item).trim())

      if (values.length) {
        clean[key] = values
      }
    }
  }

  return Object.keys(clean).length ? clean : null
}

export const saveProductViewContext = ({
  productId,
  categoryId = null,
  position = null,
  page = null,
  filters = null,
  path = null,
  block = null,
}) => {
  if (!canUseStorage() || !productId) {
    return
  }

  try {
    const normalizedFilters = normalizeFilters(filters) || filtersFromCategoryPath(path)

    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      product_id: Number(productId),
      category_id: categoryId != null ? Number(categoryId) : null,
      position: position != null ? Number(position) : null,
      page: page != null ? Number(page) : null,
      filters: normalizedFilters,
      block: KNOWN_BLOCKS.has(block) ? block : null,
      source_url: currentPageUrl(),
      ts: Date.now(),
    }))
  } catch {
    // ignore quota / private mode
  }
}

export const consumeProductViewContext = (productId) => {
  if (!canUseStorage() || !productId) {
    return null
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    localStorage.removeItem(STORAGE_KEY)

    if (!raw) {
      return null
    }

    const ctx = JSON.parse(raw)
    if (!ctx || Number(ctx.product_id) !== Number(productId)) {
      return null
    }

    if (!ctx.ts || Date.now() - Number(ctx.ts) > TTL_MS) {
      return null
    }

    return {
      category_id: ctx.category_id ?? null,
      position: ctx.position ?? null,
      page: ctx.page ?? null,
      filters: normalizeFilters(ctx.filters),
      block: KNOWN_BLOCKS.has(ctx.block) ? ctx.block : null,
      source_url: typeof ctx.source_url === 'string' ? ctx.source_url : null,
    }
  } catch {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // ignore
    }
    return null
  }
}
